/**
 * Shared hook for hierarchical scope visibility.
 *
 * Combines the logged-in user's role (authStore) with the current viewing
 * context (contextStore) to resolve which distributors, MSPs (MSPs), and
 * companies the UI should display and filter by.
 *
 * Hierarchy: platform_admin → distributors → MSPs (MSPs) → companies
 *            distributor_admin → MSPs under their distributor → companies
 *            msp_admin → companies under their MSP
 *            company roles → single company
 */

import { useCallback, useMemo } from "react";
import { useAuthStore } from "@/store/authStore";
import { useContextStore } from "@/store/contextStore";
import { Role } from "@/lib/constants/roles";
import { type Distributor } from "@/lib/api/distributors";
import { type MSP } from "@/lib/api/msps";
import { type Company } from "@/lib/api/companies";
import { useScopeData } from "@/hooks/scopeData";
import { platformAssignment } from "@/lib/auth/jwt";

/**
 * Upper bound for scope-selector list fetches. A page that comes back with
 * exactly this many rows may have more behind it, so we treat it as *partial*
 * (the completeness check below). Exported so tests can construct a full page.
 */
// Re-exported from its owner so the constant has one definition.

/** Compare API ids that may be string or number depending on endpoint/serializer. */
function sameId(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export interface ScopeHierarchy {
  /** Distributors visible (system admin gets all; distributor gets theirs). */
  distributors: Distributor[];
  /** MSPs (MSPs) visible after applying distributor filter. */
  msps: MSP[];
  /** Companies visible after applying distributor + MSP filter. */
  companies: Company[];

  /** Currently selected distributor (from context or auth). `null` = all. */
  selectedDistributorId: string | null;
  /** Currently selected MSP (MSP). `null` = all visible. */
  selectedMspId: string | null;
  /** Currently selected company. `null` = all visible. */
  selectedCompanyId: string | null;

  /** Pick a distributor (system admin + distributor role). Resets MSP/company. */
  pickDistributor: (id: string | null) => void;
  /** Pick a MSP (MSP). Resets company. */
  pickMsp: (id: string | null) => void;
  /** Pick a company. */
  pickCompany: (id: string | null) => void;
  /** Clear all scope back to default for the role. */
  clearScope: () => void;

  /** Human-readable label of the current scope. */
  scopeLabel: string;

  isSystemAdmin: boolean;
  isDistributor: boolean;
  isMSPAdmin: boolean;
  isCompanyLevel: boolean;

  /**
   * Whether the role should fetch distributors from the API. System admins
   * get all; distributor admins get their own (single implicit distributor).
   * Used to gate the distributors API call and populate scope data. The
   * ScopeSelector uses ``canSwitchDistributor`` (system admin only) to
   * decide whether to render an interactive picker vs a read-only label.
   */
  canSeeDistributors: boolean;
  /** System admin + distributor only (MSP admins have a single implicit MSP). */
  canSeeMSPs: boolean;
  /** Whether the current role can switch distributor context. */
  canSwitchDistributor: boolean;
  /** System admin + distributor only (aligned with MSP picker visibility). */
  canSwitchMsp: boolean;

  /** Trigger a re-fetch of distributors, MSPs, and companies. */
  refreshScope: () => void;

  isLoading: boolean;
  error: string | null;
}

export function useScopeHierarchy(): ScopeHierarchy {
  const { user, accessToken } = useAuthStore();

  // Extract individual stable action/state selectors to avoid re-creating
  // callbacks whenever the whole store object reference changes.
  const ctxScope = useContextStore((s) => s.scope);
  const ctxDistributorId = useContextStore((s) => s.distributorId);
  const ctxMspId = useContextStore((s) => s.mspId);
  const ctxCompanyId = useContextStore((s) => s.companyId);
  // Names persisted alongside the ids — used to synthesize a dropdown option for
  // the current selection when the fetched list doesn't contain it (otherwise
  // the Select would render blank on its own value).
  const ctxMspName = useContextStore((s) => s.mspName);
  const setAllContext = useContextStore((s) => s.setAllContext);
  const setDistributorContext = useContextStore((s) => s.setDistributorContext);
  const setMspContext = useContextStore((s) => s.setMspContext);
  const setCompanyContext = useContextStore((s) => s.setCompanyContext);
  const clearContext = useContextStore((s) => s.clearContext);

  const isSystemAdmin =
    user?.role === Role.PLATFORM_ADMIN || user?.role === Role.PLATFORM_MANAGER;
  const isAccountManager = user?.role === Role.PLATFORM_MANAGER;
  const isDistributor = user?.role === Role.DISTRIBUTOR_ADMIN;
  const isMSPAdmin = user?.role === Role.MSP_ADMIN;
  const isCompanyLevel = !isSystemAdmin && !isDistributor && !isMSPAdmin;

  // Memoized because the `?? []` fallback minted a fresh array on every render,
  // which propagated into the useMemo hooks below and made them recompute each
  // time — the identity, not the value, was changing.
  const allowedDistributorIds = useMemo(
    () => user?.allowedDistributorIds ?? [],
    [user?.allowedDistributorIds],
  );
  const allowedMspIds = useMemo(
    () => user?.allowedMspIds ?? [],
    [user?.allowedMspIds],
  );

  const canSeeDistributors = isSystemAdmin || isDistributor;
  const canSeeMSPs = isSystemAdmin || isDistributor;
  // Platform staff limited to organizations that include no distributor (a
  // manager over one MSP) have none to switch between: the bar's distributor
  // list held only "All", so their bar starts at the MSP.
  const assignedOrganizationIds = platformAssignment(user?.role, accessToken);
  const restrictedStaff =
    isSystemAdmin &&
    (allowedDistributorIds.length > 0 ||
      allowedMspIds.length > 0 ||
      (assignedOrganizationIds?.length ?? 0) > 0);
  const canSwitchDistributor =
    isSystemAdmin && (!restrictedStaff || allowedDistributorIds.length > 0);
  const canSwitchMsp = isSystemAdmin || isDistributor;

  // A MSP admin has a single implicit MSP, so `canSeeMSPs` (which gates the
  // MSP switcher) stays false for them. They still need the MSP list fetched so
  // their own MSP resolves into `visibleMsps`, which in turn admits their
  // companies into `visibleCompanies` and names the MSP breadcrumb. The backend
  // narrows /MSPs/ to their one MSP.
  const needsMspList = canSeeMSPs || isMSPAdmin;

  // The lists themselves come from one shared, cached fetch rather than a
  // per-instance one. See `scopeData.ts`: this hook has 29 call sites, and each
  // used to run its own request and settle on its own clock.
  const {
    distributors,
    msps,
    companies,
    mspsComplete,
    isLoading,
    error,
    refresh: refreshScope,
  } = useScopeData({
    userId: user?.id != null ? String(user.id) : null,
    isPlatformStaff: isSystemAdmin,
    canSeeDistributors,
    needsMspList,
    isCompanyLevel,
  });

  // Effective distributor (kept when drilling to MSP / company so the bar does not reset)
  const selectedDistributorId = useMemo(() => {
    if (
      ctxDistributorId &&
      (ctxScope === "distributor" ||
        ctxScope === "msp" ||
        ctxScope === "company")
    ) {
      return ctxDistributorId;
    }
    if (isDistributor) {
      return user?.distributorId ?? null;
    }
    return null;
  }, [
    ctxScope,
    ctxDistributorId,
    isDistributor,
    user?.distributorId,
  ]);

  // Effective MSP
  const selectedMspId = useMemo(() => {
    if ((ctxScope === "msp" || ctxScope === "company") && ctxMspId)
      return ctxMspId;
    if (isMSPAdmin) {
      return user?.mspId ?? null;
    }
    return null;
  }, [ctxScope, ctxMspId, isMSPAdmin, user?.mspId]);

  // Effective company
  const selectedCompanyId = useMemo(() => {
    if (ctxScope === "company" && ctxCompanyId) return ctxCompanyId;
    if (isCompanyLevel) {
      return user?.companyId ?? null;
    }
    return null;
  }, [ctxScope, ctxCompanyId, isCompanyLevel, user?.companyId]);

  // Visible distributors
  const visibleDistributors = useMemo(() => {
    if (isSystemAdmin) {
      if (isAccountManager && allowedDistributorIds.length > 0) {
        return distributors.filter((d) =>
          allowedDistributorIds.some((aid) => sameId(aid, d.id)),
        );
      }
      return distributors;
    }
    if (isDistributor) {
      const distId = user?.distributorId;
      if (distId) {
        return distributors.filter((d) => sameId(d.id, distId));
      }
    }
    return [];
  }, [
    distributors,
    isSystemAdmin,
    isAccountManager,
    allowedDistributorIds,
    isDistributor,
    user?.distributorId,
  ]);

  // Visible MSPs (filtered by distributor selection)
  const visibleMsps = useMemo(() => {
    let pool = msps;
    // ``authNarrowed`` = the pool was filtered by an authorization/hierarchy
    // rule (allowed MSPs, a selected distributor, a distributor/MSP admin's own
    // org, or an intentional empty). It is FALSE only for the full unrestricted
    // list (system admin, no distributor). We preserve a missing selection ONLY
    // when the list is NOT auth-narrowed — i.e. the selection is absent because
    // the list is incomplete/still loading, not because auth excluded it. This
    // is what stops a stale/localStorage-edited foreign id from being resurrected
    // past a restriction.
    let authNarrowed = true;
    const accountManagerScoped = isAccountManager && allowedMspIds.length > 0;
    if (accountManagerScoped) {
      pool = pool.filter((t) =>
        allowedMspIds.some((aid) => sameId(aid, t.id)),
      );
    }
    if (isSystemAdmin) {
      if (selectedDistributorId) {
        const dMeta = distributors.find((di) =>
          sameId(di.id, selectedDistributorId),
        );
        pool = pool.filter((t) => {
          if (sameId(t.distributor, selectedDistributorId)) return true;
          const orphan =
            t.distributor == null ||
            t.distributor === "" ||
            t.distributor === undefined;
          if (orphan && dMeta?.is_default) return true;
          return false;
        });
      } else if (!accountManagerScoped) {
        // Full platform list, no filter applied — a missing selection here is a
        // data gap, so it may be preserved.
        authNarrowed = false;
      }
    } else if (isDistributor) {
      const distId = user?.distributorId;
      if (distId) {
        const dMeta = distributors.find((di) => sameId(di.id, distId));
        pool = pool.filter((t) => {
          if (sameId(t.distributor, distId)) return true;
          const orphan =
            t.distributor == null ||
            t.distributor === "" ||
            t.distributor === undefined;
          if (orphan && dMeta?.is_default) return true;
          return false;
        });
      } else {
        pool = [];
      }
    } else if (isMSPAdmin) {
      const mspId = user?.mspId;
      pool = mspId ? pool.filter((t) => sameId(t.id, mspId)) : [];
    } else {
      pool = [];
    }
    // Do not narrow `MSPs` to the selected MSP — keeps MSP dropdown usable; pages use selectedMspId for data filters.
    // Preserve the current selection only when the list was NOT auth-narrowed
    // (see above): renders its own value across an incomplete fetch, without
    // reinserting an id that a restriction deliberately excluded.
    if (
      !authNarrowed &&
      selectedMspId &&
      !pool.some((t) => sameId(t.id, selectedMspId))
    ) {
      pool = [
        ...pool,
        { id: selectedMspId, name: ctxMspName ?? "Selected MSP" } as MSP,
      ];
    }
    return pool;
  }, [
    msps,
    distributors,
    isSystemAdmin,
    isAccountManager,
    allowedMspIds,
    isDistributor,
    isMSPAdmin,
    user,
    selectedDistributorId,
    selectedMspId,
    ctxMspName,
  ]);

  // Visible companies (filtered by visible MSPs + selected company)
  const visibleCompanies = useMemo(() => {
    let pool = companies;
    const visibleMspIds = new Set(visibleMsps.map((t) => String(t.id)));
    // The MSP-visibility gate. Apply it for non-system-admins (always) and for a
    // system admin drilled into a distributor — but ONLY when the MSP list is
    // KNOWN COMPLETE (``mspsComplete``). A partial or still-loading list must not
    // gate: a company whose MSP was not in the fetched page would be wrongly
    // hidden. Backend hierarchy scope enforces isolation regardless, so this is
    // defense-in-depth, correctly disabled while data is incomplete.
    if ((!isSystemAdmin || selectedDistributorId) && mspsComplete) {
      pool = pool.filter((c) => visibleMspIds.has(String(c.msp)));
    }
    // When an MSP is selected, additionally narrow directly by it. This survives
    // an empty MSP list (unlike the gate above) so the selected MSP's companies
    // always render.
    if (selectedMspId) {
      pool = pool.filter((c) => sameId(c.msp, selectedMspId));
    }
    // Do not narrow `companies` to the selected company — keeps Company dropdown
    // usable; pages filter their data via selectedCompanyId instead.
    if (isCompanyLevel) {
      const companyId = user?.companyId;
      pool = companyId ? pool.filter((c) => sameId(c.id, companyId)) : [];
    }
    // NOTE: no synthetic "preserve the selected company" append here. Every path
    // that could omit the selected company (isCompanyLevel narrowing, the MSP
    // gate, or the selectedMspId filter) is an intentional auth/hierarchy
    // exclusion — reinserting the id would defeat it, and let a stale/edited
    // localStorage company id appear as an option. The selected company already
    // survives the legitimate drill-in flow because it belongs to selectedMspId.
    return pool;
  }, [
    companies,
    visibleMsps,
    mspsComplete,
    isSystemAdmin,
    isCompanyLevel,
    selectedDistributorId,
    selectedMspId,
    user?.companyId,
  ]);

  // The widest view this caller may have. The server lets only platform staff
  // view the whole platform and refuses `all` from anyone else, which failed
  // every request after it; their widest view is their own default scope.
  const widenToTop = useCallback(() => {
    if (isSystemAdmin) setAllContext();
    else clearContext();
  }, [isSystemAdmin, setAllContext, clearContext]);

  // Scope actions
  const pickDistributor = useCallback(
    (id: string | null) => {
      if (!id) {
        widenToTop();
      } else {
        const d = distributors.find((x) => sameId(x.id, id));
        if (d) setDistributorContext(String(d.id), d.name);
      }
    },
    [widenToTop, setDistributorContext, distributors],
  );

  const pickMsp = useCallback(
    (id: string | null) => {
      if (!id) {
        const snap = useContextStore.getState();
        // Prefer persisted distributor in store (avoids find() miss → setAllContext wiping distributor)
        if (snap.distributorId) {
          snap.narrowToDistributorScope();
          return;
        }
        if (selectedDistributorId) {
          const d = distributors.find((x) =>
            sameId(x.id, selectedDistributorId),
          );
          if (d) setDistributorContext(String(d.id), d.name);
          else widenToTop();
        } else {
          widenToTop();
        }
      } else {
        const t = msps.find((x) => sameId(x.id, id));
        if (t) setMspContext(String(t.id), t.name);
      }
    },
    [
      widenToTop,
      setDistributorContext,
      setMspContext,
      msps,
      distributors,
      selectedDistributorId,
    ],
  );

  const pickCompany = useCallback(
    (id: string | null) => {
      if (!id) {
        // "All companies" → stay on current MSP + distributor (same pattern as All MSPs → distributor)
        const snap = useContextStore.getState();
        // With neither held, narrowing falls through to `all`.
        if (!snap.mspId && !snap.distributorId) {
          widenToTop();
          return;
        }
        snap.narrowToMspScope();
      } else {
        const c = companies.find((x) => sameId(x.id, id));
        if (c) setCompanyContext(String(c.id), c.name, c.msp, c.msp_name);
      }
    },
    [setCompanyContext, companies, widenToTop],
  );

  const clearScope = useCallback(() => {
    if (isDistributor) {
      const distId = user?.distributorId;
      if (distId) {
        const d = distributors.find((x) => sameId(x.id, distId));
        if (d) {
          setDistributorContext(String(d.id), d.name);
          return;
        }
      }
    }
    clearContext();
  }, [
    setDistributorContext,
    clearContext,
    isDistributor,
    user?.distributorId,
    distributors,
  ]);

  // Label for the current scope
  const scopeLabel = useMemo(() => {
    if (selectedCompanyId) {
      const c = companies.find((x) => sameId(x.id, selectedCompanyId));
      return c ? c.name : "Company";
    }
    if (selectedMspId) {
      const t = msps.find((x) => sameId(x.id, selectedMspId));
      return t ? t.name : "MSP";
    }
    if (selectedDistributorId) {
      const d = distributors.find((x) => sameId(x.id, selectedDistributorId));
      return d ? d.name : "Distributor";
    }
    if (isSystemAdmin) return "All (Platform)";
    return "My scope";
  }, [
    selectedDistributorId,
    selectedMspId,
    selectedCompanyId,
    distributors,
    msps,
    companies,
    isSystemAdmin,
  ]);

  return {
    distributors: visibleDistributors,
    msps: visibleMsps,
    companies: visibleCompanies,
    selectedDistributorId,
    selectedMspId,
    selectedCompanyId,
    pickDistributor,
    pickMsp,
    pickCompany,
    clearScope,
    refreshScope,
    scopeLabel,
    isSystemAdmin: Boolean(isSystemAdmin),
    isDistributor: Boolean(isDistributor),
    isMSPAdmin: Boolean(isMSPAdmin),
    isCompanyLevel,
    canSeeDistributors,
    canSeeMSPs: Boolean(canSeeMSPs),
    canSwitchDistributor,
    canSwitchMsp,
    isLoading,
    error,
  };
}
